import ThemeControl from "@/components/ui/ThemeControl";
import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import usePageMotion from "../../hooks/usePageMotion";
import GradientNavHp from "../../components/ui/gradient-nav";
import { useQueryClient } from "@tanstack/react-query";
import { LayoutDashboard, BookOpen, Landmark, Wallet, HandCoins, Receipt, ReceiptText, Boxes, Building2, PiggyBank, Users, FileBarChart2, ClipboardCheck, ShieldCheck, ScrollText, Settings, LogOut, ArrowLeft, Menu, X, PlusCircle, Building, Target, Scale, PieChart, FileStack, Percent, Smartphone, Warehouse, FileSearch, CreditCard } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import NavBersarang, { saringNavMenurutIzin } from "../../components/finance/NavBersarang";

const NAV = [
  { section: "Utama", items: [
    { to: "/finance", end: true, icon: LayoutDashboard, label: "Dashboard", tid: "fin-nav-dashboard", modKey: "finance_dashboard" },
    { to: "/finance/catat", icon: PlusCircle, label: "Catat Cepat", tid: "fin-nav-catat", accent: true, modKey: "finance_catat" },
  ]},
  { section: "Kas & Bank", items: [
    { to: "/finance/kas-bank", icon: Wallet, label: "Kas & Bank", tid: "fin-nav-kasbank", modKey: "finance_kas_bank" },
    { to: "/finance/petty-cash", icon: PiggyBank, label: "Petty Cash", tid: "fin-nav-petty", modKey: "finance_petty" },
    { to: "/finance/rekonsiliasi", icon: Landmark, label: "Rekonsiliasi Bank", tid: "fin-nav-rekon", modKey: "finance_rekonsiliasi" },
    { to: "/finance/cash-opname", icon: Scale, label: "Cash Opname", tid: "fin-nav-opname", modKey: "finance_cash_opname" },
  ]},
  { section: "Piutang & Utang", items: [
    // Brief owner 22 Sep 2026 (C16): "Piutang (AR)" menjadi parent dua anak
    // dengan izin MASING-MASING — Claim MDR memuat nama konsumen & IMEI kredit,
    // jadi tidak ikut izin Piutang. tid lama `fin-nav-piutang` kini tombol
    // parent-nya (pola `fin-nav-cashback-program`); daunnya punya tid baru.
    { tid: "fin-nav-piutang", icon: HandCoins, label: "Piutang (AR)", children: [
      { to: "/finance/piutang", icon: HandCoins, label: "Piutang Usaha", tid: "fin-nav-piutang-usaha", modKey: "finance_piutang" },
      { to: "/finance/claim-mdr", icon: CreditCard, label: "Claim MDR", tid: "fin-nav-claim-mdr", modKey: "finance_claim_mdr" },
    ]},
    { to: "/finance/utang", icon: Receipt, label: "Utang (AP)", tid: "fin-nav-utang", modKey: "finance_utang" },
    // Brief 22 Sep 2026 (H2): tagihan dari PO saja, SATU buku dengan Utang — jadi kunci izinnya sama.
    { to: "/finance/pelunasan-pembelian", icon: Boxes, label: "Pelunasan Pembelian", tid: "fin-nav-pelunasan-pembelian", modKey: "finance_utang" },
    { to: "/finance/credit-note", icon: FileStack, label: "Credit Note", tid: "fin-nav-credit-note", modKey: "finance_credit_note" },
    // IMEI Cashback / Price Protection (batch 11 Agu 2026) — SATU kunci izin
    // `finance_cashback_claim` menaungi DUA halaman (Program & Claim Batch),
    // pola sama dengan `inventory_po` yang menaungi 4 halaman (lihat PETA-MODUL.md).
    // Brief owner 22 Sep 2026 (A): "Program Price Protection" menjadi PARENT
    // 3 tingkat di posisi yang sama (tid lama `fin-nav-cashback-program` kini
    // tombol parent-nya). Kunci izin tetap satu — semua daun `modKey` yang
    // SAMA dengan `require_permission` endpoint `/cashback/*`.
    { tid: "fin-nav-cashback-program", icon: Percent, label: "Program Price Protection", children: [
      { to: "/finance/price-protection/dashboard", icon: LayoutDashboard, label: "Dashboard", tid: "fin-nav-pp-dashboard", modKey: "finance_cashback_claim" },
      { tid: "fin-nav-pp-aktivasi", label: "Program Support Aktivasi", children: [
        { to: "/finance/price-protection/aktivasi/toko", label: (k) => `Support Aktivasi ${k.namaToko}`, tid: "fin-nav-pp-aktivasi-toko", modKey: "finance_cashback_claim" },
        { to: "/finance/price-protection/aktivasi/dealer", label: "Support Aktivasi Dealer Partner", tid: "fin-nav-pp-aktivasi-dealer", modKey: "finance_cashback_claim" },
      ]},
      { tid: "fin-nav-pp-cashback", label: "Program Support Cashback", children: [
        { to: "/finance/price-protection/cashback/toko", label: (k) => `Support Cashback ${k.namaToko}`, tid: "fin-nav-pp-cashback-toko", modKey: "finance_cashback_claim" },
        { to: "/finance/price-protection/cashback/dealer", label: "Support Cashback Dealer Partner", tid: "fin-nav-pp-cashback-dealer", modKey: "finance_cashback_claim" },
      ]},
    ]},
    { to: "/finance/cashback/claims", icon: Smartphone, label: "Cashback Claim (IMEI)", tid: "fin-nav-cashback-claim", modKey: "finance_cashback_claim" },
    { to: "/finance/beban", icon: Receipt, label: "Beban Usaha", tid: "fin-nav-beban", modKey: "finance_beban" },
    { to: "/finance/reimbursement", icon: ReceiptText, label: "Reimbursement", tid: "fin-nav-reimbursement", modKey: "finance_reimbursement" },
  ]},
  { section: "Aset & Persediaan", items: [
    { to: "/finance/persediaan", icon: Boxes, label: "Persediaan", tid: "fin-nav-persediaan", modKey: "finance_persediaan" },
    { to: "/finance/aset-tetap", icon: Building, label: "Aset Tetap", tid: "fin-nav-aset", modKey: "finance_aset_tetap" },
    { to: "/finance/dividen", icon: Users, label: "Dividen", tid: "fin-nav-dividen", modKey: "finance_dividen" },
  ]},
  { section: "Laporan & Kontrol", items: [
    { to: "/finance/laporan", icon: FileBarChart2, label: "Laporan Keuangan", tid: "fin-nav-laporan", modKey: "finance_laporan" },
    { to: "/finance/buku-besar", icon: BookOpen, label: "Buku Besar", tid: "fin-nav-buku-besar", modKey: "finance_laporan" },
    { to: "/finance/jurnal", icon: FileSearch, label: "Jurnal per Transaksi", tid: "fin-nav-jurnal", modKey: "finance_laporan" },
    { to: "/finance/laba-kategori", icon: PieChart, label: "Laba per Kategori", tid: "fin-nav-laba-kategori", modKey: "finance_laporan" },
    { to: "/finance/laba-gudang", icon: Warehouse, label: "Laba per Gudang", tid: "fin-nav-laba-gudang", modKey: "finance_laporan" },
    { to: "/finance/budget", icon: Target, label: "Budget vs Actual", tid: "fin-nav-budget", modKey: "finance_budget" },
    { to: "/finance/coa", icon: BookOpen, label: "Chart of Accounts", tid: "fin-nav-coa", modKey: "finance_coa" },
    { to: "/finance/approval", icon: ClipboardCheck, label: "Approval SPV", tid: "fin-nav-approval", modKey: "finance_approval" },
    { to: "/finance/audit", icon: ShieldCheck, label: "Audit Trail", tid: "fin-nav-audit", modKey: "finance_audit" },
    { to: "/finance/pengaturan", icon: Settings, label: "Pengaturan", tid: "fin-nav-pengaturan", modKey: "finance_pengaturan" },
  ]},
];

export default function FinanceLayout() {
  // branding dari AuthContext — jangan fetch /config/branding sendiri di sini
  // (dulu jadi request duplikat ketiga di halaman finance; brief bos, laporan performa).
  const { user, logout, can, branding } = useAuth();
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const pageRef = useRef(null);
  usePageMotion(pageRef, location.pathname + location.search);
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  // Bersihkan cache React Query sebelum keluar: tombol ini berpindah tanpa memuat
  // ulang halaman, jadi tanpa clear() pengguna berikutnya di tab yang sama masih
  // memegang data keuangan pengguna sebelumnya. Sama dengan doLogout di Layout.jsx.
  const doLogout = () => { queryClient.clear(); logout(); nav("/login"); };
  const isFinanceRole = user?.role?.startsWith("finance_");
  // Filter NAV: hanya tampilkan item yang user boleh lihat (can(modKey, "view")).
  // Rekursif sejak menu bersarang (brief 22 Sep 2026): parent yang semua anaknya
  // tersaring ikut hilang, bukan jadi tombol buntu.
  const visibleNav = NAV
    .map(sec => ({ ...sec, items: saringNavMenurutIzin(sec.items, can) }))
    .filter(sec => sec.items.length > 0);
  // Nama toko sendiri dari Branding (white-label, keputusan A1) untuk label submenu.
  const konteksNav = { namaToko: branding?.nama_toko_sendiri || "EppleHub" };

  return (
    <div className="eh-workspace min-h-screen flex bg-[#F8F9FA]">
      <aside
        data-testid="finance-sidebar"
        className={`eh-sidebar fixed lg:sticky top-0 left-0 z-40 w-64 h-screen shrink-0 bg-[#0F172A] text-white flex flex-col transition-transform duration-300 lg:translate-x-0 ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center shrink-0 shadow-lg shadow-emerald-500/25">
              <Landmark className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="font-display font-bold text-sm leading-tight truncate">Finance</p>
              <p className="text-[11px] text-white/50">{branding.app_name || "EppleHub Operational System"}</p>
            </div>
          </div>
          <button onClick={() => setDrawerOpen(false)} data-testid="fin-close-drawer" className="lg:hidden p-1.5 rounded-md hover:bg-white/10 text-white/60 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-3 space-y-4 overflow-y-auto">
          {/* Dipindah ke atas (brief owner 19 Agu poin 7) — dulu duduk di footer
              dekat tombol Keluar, di bawah SEMUA menu Finance, jadi staf yang cuma
              mampir sebentar harus scroll melewati seluruh sidebar untuk kembali ke
              HRIS. Sekarang jadi hal pertama yang terlihat begitu sidebar dibuka. */}
          {!isFinanceRole && (
            <button
              data-testid="fin-back-hris"
              onClick={() => nav("/")}
              className="w-full inline-flex items-center gap-2 px-3 h-9 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white text-sm transition-colors border border-white/10"
            >
              <ArrowLeft className="w-4 h-4" />
              Kembali ke HRIS
            </button>
          )}
          {visibleNav.map(({ section, items }) => (
            <div key={section}>
              <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-white/40">{section}</p>
              <div className="space-y-0.5">
                {items.map(({ to, end, icon: Icon, label, tid, accent, children }) => children ? (
                  <NavBersarang key={tid} item={{ tid, icon: Icon, label, children }} konteks={konteksNav} />
                ) : (
                  <NavLink
                    key={to} to={to} end={end} data-testid={tid}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        accent
                          ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 font-medium border border-emerald-500/30"
                          : isActive
                            ? "bg-white/10 text-white font-medium"
                            : "text-white/60 hover:text-white hover:bg-white/5"
                      }`
                    }
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-white/10 space-y-1.5">
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="w-9 h-9 rounded-full bg-emerald-600/20 text-emerald-300 grid place-items-center text-sm font-semibold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[11px] text-white/50 capitalize">{user?.role?.replace(/_/g, " ")}</p>
            </div>
          </div>
          <ThemeControl />
          <button
            data-testid="fin-btn-logout"
            onClick={() => { if (confirm("Keluar dari akun?")) doLogout(); }}
            className="w-full inline-flex items-center gap-2 px-3 py-2.5 rounded-lg bg-rose-600/10 hover:bg-rose-600/25 text-rose-300 hover:text-rose-100 text-sm font-medium transition-colors border border-rose-500/20"
          >
            <LogOut className="w-4 h-4" />
            Keluar
          </button>
        </div>
      </aside>

      {drawerOpen && <div onClick={() => setDrawerOpen(false)} className="eh-shell-backdrop fixed inset-0 z-30 bg-black/50 backdrop-blur-sm lg:hidden" />}

      <div className="eh-workspace-body flex-1 flex flex-col min-w-0">
        <header className="eh-topbar lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 flex items-center gap-3 px-4 py-3">
          <button onClick={() => setDrawerOpen(true)} data-testid="fin-open-drawer" className="p-2 -ml-2 rounded-md hover:bg-slate-100 text-slate-700">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center shrink-0">
              <Landmark className="w-4 h-4 text-white" />
            </div>
            <p className="font-display font-bold text-sm text-slate-900 truncate">Finance</p>
          </div>
        </header>
        <main ref={pageRef} className="eh-content flex-1 overflow-x-hidden" data-testid="finance-page-content">
          <Outlet />
          <GradientNavHp />
        </main>
      </div>
    </div>
  );
}
